import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { themeColor } from '../theme/tokens';

// Light / dark theme for the merchant dashboard (CB-16, DR-45). A copy of
// website/src/shell/theme-context.tsx adapted to this surface's storage key and
// token map (CB-D12 — duplicated, not shared).
//
// One mechanism: `data-theme` on <html> is always written ('light' | 'dark'). Light is
// the default; only an explicit stored 'dark' switches — the OS colour scheme is never
// consulted. The preference is a per-device convenience kept in localStorage (no
// account setting exists for it — never a token or identity, AP-D4).
export type ThemePref = 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** What the merchant chose. */
  pref: ThemePref;
  /** What is showing now (always equal to pref). */
  resolved: ResolvedTheme;
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Must match the pre-paint script in index.html.
export const THEME_STORAGE_KEY = 'bd.merchant.theme';

export function readStoredPref(): ThemePref {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readStoredPref);
  const resolved: ResolvedTheme = pref;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', pref);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themeColor[pref]);
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* storage blocked — the choice still applies for this session */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ pref, resolved, setPref }),
    [pref, resolved, setPref],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
